<template>
  <SlotWrapper>
    <template #body>
      <div>
        <service-category-form
          v-model="formData.service_category_id"
        ></service-category-form>
        <divider></divider>
        <duration-form
          v-model="formData.duration_minutes"
          :appointment="appointment"
          :selected-service-category="selectedServiceCategory"
          :start-time="startTime"
          @clear="resetDuration"
        ></duration-form>
        <divider></divider>
        <eligible-technicians-form
          v-model="formData.user_id"
          :service-category-id="formData.service_category_id"
          :pinned-technician-id="pinnedTechnicianId"
          :pinned-reason="originalValues.pinned_reason"
          @update-pinned-reason="setPinnedReason"
        ></eligible-technicians-form>
        <divider></divider>
        <availability-form
          :scheduled-time="appointment.scheduled_time"
          :arrival_window_start="appointment.arrival_window_start"
          :arrival_window_end="appointment.arrival_window_end"
          data-vv-name="Scheduled"
          :service-category-id="formData.service_category_id"
          :technician-id="formData.user_id"
          :duration-minutes="formData.duration_minutes"
          :timezone="appointment.address.time_zone"
          :postal-code="appointment.address.zip_code"
          :current-scheduled-time="appointment.scheduled_time"
          allow-do-not-notify
          @do-not-notify="setDoNotNotify"
          @update:data="updateData"
        ></availability-form>
        <change-summary
          :original="originalValues"
          :changes="changes"
        ></change-summary>
      </div>
    </template>
  </SlotWrapper>
</template>

<script>
import moment from "moment-timezone";
import { mapState } from "vuex";
import { getObjDiff, getProperty } from "@/lib/helpers.js";

import Divider from "@/components/Divider.vue";
import SlotWrapper from "@/components/SlotWrapper";
import useVuelidate from "@vuelidate/core";
import ChangeSummary from "../../ChangeSummary.vue";
import AvailabilityForm from "../../forms/AvailabilityForm.vue";
import DurationForm from "../../forms/DurationForm.vue";
import EligibleTechniciansForm from "../../forms/EligibleTechniciansForm.vue";
import ServiceCategoryForm from "../../forms/ServiceCategoryForm.vue";

export default {
  components: {
    SlotWrapper,
    DurationForm,
    Divider,
    EligibleTechniciansForm,
    AvailabilityForm,
    ServiceCategoryForm,
    ChangeSummary
  },
  props: {
    appointment: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ["changes-update"],
  data() {
    return {
      formData: null
    };
  },
  computed: {
    ...mapState({
      serviceCategories: "serviceCategories",
      selectedServiceCategory({ serviceCategories }) {
        return serviceCategories.find(
          (sc) => sc.id === this.formData.service_category_id
        );
      }
    }),
    pinnedTechnicianId() {
      const { assignment_pinned, technician } = this.appointment;
      return (assignment_pinned && technician && technician.user_id) || null;
    },
    originalValues() {
      const {
        service_category,
        technician,
        assignment_pinned,
        estimated_end_time,
        pinned_reason
      } = this.appointment;
      return {
        ...this.appointment,
        pinned_reason,
        estimated_end_time: moment(estimated_end_time).format("h:mm a"),
        service_category_id: getProperty(service_category, "id"),
        user_id: assignment_pinned ? getProperty(technician, "user_id") : null,
        skip_notifications: false
      };
    },
    changes() {
      if (!this.formData) {
        return {};
      }
      const changes = getObjDiff(this.originalValues, this.formData);
      // ssdev-4103 dont show estimated end time changes when rescheduling
      if (!Object.prototype.hasOwnProperty.call(changes, "duration_minutes")) {
        delete changes.estimated_end_time;
      }
      return changes;
    },
    startTime() {
      const { dispatch_time, min_arrival_time, arrival_window_minutes } =
        this.appointment;

      const { scheduled_time } = this.formData;

      // If availability has been changed, use new scheduled time
      // else use actual dispatch time or min arrival time
      return scheduled_time
        ? moment(scheduled_time)
            .subtract(arrival_window_minutes, "minutes")
            .format()
        : moment(dispatch_time || min_arrival_time).format();
    }
  },
  watch: {
    "formData.service_category_id"(newValue, oldValue) {
      if (newValue !== oldValue && oldValue) {
        // Update duration when service category is changed
        if (this.selectedServiceCategory) {
          this.formData.duration_minutes =
            this.selectedServiceCategory.duration_minutes;
        }
      }
    },
    "formData.duration_minutes"(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setEndTime();
      }
    },
    changes(newValue) {
      this.$emit("changes-update", newValue);
    }
  },
  created() {
    this.setFormData();
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  methods: {
    getChanges() {
      return this.changes;
    },
    setFormData() {
      if (this.appointment) {
        const {
          duration_minutes,
          service_category,
          technician,
          scheduled_time,
          arrival_window_start,
          arrival_window_end,
          assignment_pinned,
          estimated_end_time,
          pinned_reason
        } = this.appointment;
        this.formData = {
          service_category_id: getProperty(service_category, "id"),
          duration_minutes,
          user_id: assignment_pinned
            ? getProperty(technician, "user_id")
            : null,
          scheduled_time,
          arrival_window_start,
          arrival_window_end,
          estimated_end_time: moment(estimated_end_time).format("h:mm a"),
          pinned_reason,
          skip_notifications: false
        };
      }
    },
    resetDuration() {
      // If the appointment is not pending and has a duration
      // and the service category has changed, use it's duration
      // else use original duration from appointment
      const { duration_minutes } = this.originalValues;
      this.formData.duration_minutes =
        this.selectedServiceCategory && duration_minutes
          ? this.selectedServiceCategory.duration_minutes
          : duration_minutes;
    },
    setEndTime() {
      const {
        dispatch_time,
        min_arrival_time,
        arrival_window_minutes,
        estimated_duration_minutes
      } = this.appointment;

      const { scheduled_time, duration_minutes } = this.formData;

      // If availability has been changed, use new scheduled time
      // else use actual dispatch time or min arrival time
      const startTime = scheduled_time
        ? moment(scheduled_time).subtract(arrival_window_minutes, "minutes")
        : moment(dispatch_time || min_arrival_time);

      this.formData.estimated_end_time = moment(startTime)
        .add(duration_minutes || estimated_duration_minutes, "minutes")
        .format("h:mm a");
    },
    setPinnedReason(val) {
      this.formData.pinned_reason = val;
    },
    setDoNotNotify(val) {
      this.formData.skip_notifications = val;
    },
    updateData({ key, value }) {
      this.formData[key] = value;
    }
  }
};
</script>

<style lang="scss" scoped></style>
